import { readStorage, writeStorage } from './assistant-storage'
import {
  DEFAULT_ANCHOR,
  EDGE_MARGIN_PX,
  parseAnchor,
  placementArea,
  snapToNearestEdge,
  TOP_INSET_PX,
  type EdgeAnchor,
  type Size,
} from './edge-anchor'

/**
 * The assistant's two floating forms — the launcher bubble and the chat window — in edge-anchor
 * terms: their sizes, where each is remembered, and where each starts.
 */

export const BUBBLE_SIZE: Size = { width: 56, height: 56 }

export const BUBBLE_ANCHOR_KEY = 'gisify.assistant.bubbleAnchor'
export const PANEL_ANCHOR_KEY = 'gisify.assistant.panelAnchor'

/**
 * Where the bubble used to be remembered, as an absolute `{x, y}`. Read once to migrate a position
 * the user already chose; never written.
 */
export const LEGACY_BUBBLE_POSITION_KEY = 'gisify.assistant.bubblePosition'

const PANEL_MAX_WIDTH_PX = 352 // w-[22rem]
const PANEL_MAX_HEIGHT_PX = 512 // h-[32rem]
const PANEL_MAX_HEIGHT_FRACTION = 0.8 // max-h-[80vh]

/** The bubble never changes size; a function so it fits the drag hook's `sizeFor`. */
export function bubbleSizeFor(): Size {
  return BUBBLE_SIZE
}

/**
 * The chat window's size for a viewport: the numbers its old Tailwind classes encoded
 * (`w-[22rem] max-w-[calc(100vw-2.5rem)] h-[32rem] max-h-[80vh]`), plus a cap they lacked — never
 * taller than the placement area, so the composer cannot fall below the fold on a short window. The
 * panel applies this INLINE, so the size it snaps with is the size it renders.
 */
export function panelSizeFor(viewport: Size): Size {
  return {
    width: Math.max(0, Math.min(PANEL_MAX_WIDTH_PX, viewport.width - 2 * EDGE_MARGIN_PX)),
    height: Math.max(
      0,
      Math.min(
        PANEL_MAX_HEIGHT_PX,
        Math.floor(viewport.height * PANEL_MAX_HEIGHT_FRACTION),
        viewport.height - TOP_INSET_PX - 2 * EDGE_MARGIN_PX,
      ),
    ),
  }
}

/**
 * A position stored by the old absolute-point bubble, converted by snapping it against the current
 * window — so a user who had moved the bubble finds it on the edge it was nearest to, not back in
 * the corner. Absent or malformed values are null.
 */
export function legacyBubbleAnchor(raw: string | null, viewport: Size): EdgeAnchor | null {
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { x, y } = parsed as { x?: unknown; y?: unknown }
    if (typeof x !== 'number' || !Number.isFinite(x)) return null
    if (typeof y !== 'number' || !Number.isFinite(y)) return null

    return snapToNearestEdge({ x, y }, BUBBLE_SIZE, placementArea(viewport))
  } catch {
    return null
  }
}

/**
 * Where the bubble starts without an anchor of its own: a migrated legacy position, else the corner.
 *
 * A migrated position is saved as the bubble's own anchor, so the conversion happens ONCE — snapped
 * against the window it was first read in. Re-snapping it on every load would land the same legacy
 * point on different edges in different window widths, the non-stickiness this replaces. The legacy
 * key itself is left as it is: read to migrate, never written.
 */
export function bubbleDefaultAnchor(viewport: Size): EdgeAnchor {
  const migrated = legacyBubbleAnchor(readStorage(LEGACY_BUBBLE_POSITION_KEY), viewport)
  if (!migrated) return DEFAULT_ANCHOR

  writeStorage(BUBBLE_ANCHOR_KEY, JSON.stringify(migrated))

  return migrated
}

/**
 * Where the chat window opens until the user drags it: on the BUBBLE's anchor — the same edges, the
 * same offsets — so it grows out of the spot the bubble occupies, away from the edges the bubble is
 * stuck to. For a bubble never moved that is the bottom-right corner the window has always opened
 * in. The bubble is hidden while the window is open, so the two never overlap.
 */
export function panelDefaultAnchor(viewport: Size): EdgeAnchor {
  return parseAnchor(readStorage(BUBBLE_ANCHOR_KEY)) ?? bubbleDefaultAnchor(viewport)
}
